import React from 'react'
import { Box, Chip, Typography, useTheme } from '@material-ui/core'
import { Attachment } from '../../components'
import type { DamageDetail } from '../../hooks/useMoveOutManagement/useMoveOutManagement'
import { formatCurrency } from './utils'

interface DamageCardProps {
    damage: DamageDetail
    areaName: string
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
    pending: { bg: '#fef3c7', text: '#92400e' },
    approved: { bg: '#d1fae5', text: '#065f46' },
    disputed: { bg: '#fee2e2', text: '#991b1b' },
}

export const DamageCard: React.FC<DamageCardProps> = ({ damage, areaName }) => {
    const theme = useTheme()
    const statusColors = STATUS_COLORS[damage.status.toLowerCase()] ?? {
        bg: '#f1f5f9',
        text: '#64748b',
    }

    return (
        <Box
            style={{
                padding: '12px 16px',
                borderBottom: '1px solid #f1f5f9',
            }}
        >
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
            >
                <Box style={{ flex: 1 }}>
                    <Box
                        display="flex"
                        alignItems="center"
                        style={{ gap: 8, marginBottom: 4 }}
                    >
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {damage.description}
                        </Typography>
                        <Chip
                            label={damage.status}
                            size="small"
                            style={{
                                height: 18,
                                fontSize: '0.6rem',
                                fontWeight: 600,
                                backgroundColor: statusColors.bg,
                                color: statusColors.text,
                            }}
                        />
                        {damage.ai_generated && (
                            <Chip
                                label="AI"
                                size="small"
                                style={{
                                    height: 18,
                                    fontSize: '0.6rem',
                                    fontWeight: 700,
                                    backgroundColor: '#ede9fe',
                                    color: '#6d28d9',
                                }}
                            />
                        )}
                    </Box>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Typography variant="caption" color="textSecondary">
                            {areaName}
                        </Typography>
                        {damage.created_date && (
                            <Typography variant="caption" color="textSecondary">
                                ·{' '}
                                {new Date(
                                    damage.created_date,
                                ).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                })}
                            </Typography>
                        )}
                    </Box>
                </Box>
                {damage.total_cost != null && (
                    <Typography
                        variant="body2"
                        style={{
                            fontWeight: 600,
                            color: '#ef4444',
                            whiteSpace: 'nowrap',
                            marginLeft: 16,
                        }}
                    >
                        {formatCurrency(parseFloat(damage.total_cost))}
                    </Typography>
                )}
            </Box>

            {damage.attachments.length > 0 && (
                <Box
                    display="flex"
                    flexWrap="wrap"
                    style={{ gap: 8, marginTop: 10 }}
                >
                    {damage.attachments.map((att) => (
                        <Attachment
                            key={att.id}
                            attachment={att.url}
                            uploaded={att.uploaded}
                            theme={theme}
                            size={80}
                        />
                    ))}
                </Box>
            )}
        </Box>
    )
}
